// mobile-menu.ts
interface MobileMenuElements {
  toggle: HTMLElement | null;
  overlay: HTMLElement | null;
  panel: HTMLElement | null;
  content: HTMLElement | null;
}

export class MobileMenu {
  private elements: MobileMenuElements;
  private isOpen: boolean = false;
  private scrollLocked: boolean = false;
  private html: HTMLElement;
  private body: HTMLElement;

  constructor() {
    this.elements = {
      toggle: document.getElementById('mobile-menu-toggle'),
      overlay: document.getElementById('mobile-menu-overlay'),
      panel: document.getElementById('mobile-menu-panel'),
      content: document.getElementById('mobile-menu-content'),
    };

    this.html = document.documentElement;
    this.body = document.body;

    this.init();
  }

  private init(): void {
    this.bindEvents();
    this.setupSubmenus();
    this.setupResizeListener();
  }

  private setupResizeListener(): void {
    // Простой resize listener
    let resizeTimer: number;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        // Закрываем меню если перешли на десктоп
        if (window.innerWidth >= 992 && this.isOpen) {
          this.close();
        }
      }, 150);
    });
  }

  private bindEvents(): void {
    if (this.elements.toggle) {
      this.elements.toggle.addEventListener('click', this.toggle.bind(this));
    }

    if (this.elements.overlay) {
      this.elements.overlay.addEventListener('click', this.close.bind(this));
    }

    // Закрытие по Escape
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Закрытие при переходе по любой ссылке внутри панели (в т.ч. в профиль)
    if (this.elements.panel) {
      this.elements.panel.addEventListener('click', (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const link = target.closest('a[href]');
        if (!link || !this.elements.panel?.contains(link)) return;

        this.close();
      });
    }

    window.addEventListener('stc:barba-before-leave', () => this.close());
    window.addEventListener('stc:barba-after-enter', () => this.close());
  }

  private setupSubmenus(): void {
    // Находим все элементы с подменю
    const submenuItems = document.querySelectorAll('.mobile-nav-item.has-submenu');

    submenuItems.forEach((item: Element) => {
      const toggleBtn = item.querySelector('.mobile-submenu-toggle');
      const submenu = item.querySelector('.mobile-submenu');

      if (toggleBtn && submenu) {
        toggleBtn.addEventListener('click', (event: Event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleSubmenu(item as HTMLElement, submenu as HTMLElement);
        });
      }
    });
  }

  private toggleSubmenu(item: HTMLElement, submenu: HTMLElement): void {
    const isExpanded = submenu.classList.contains('active');

    // Переключаем текущее
    submenu.classList.toggle('active', !isExpanded);
    item.classList.toggle('active', !isExpanded);

    // Обновляем ARIA
    const toggleBtn = item.querySelector('.mobile-submenu-toggle');
    if (toggleBtn) {
      toggleBtn.setAttribute('aria-expanded', (!isExpanded).toString());
    }
  }

  private toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  private open(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    if (this.elements.toggle) {
      this.elements.toggle.setAttribute('aria-expanded', 'true');
      this.elements.toggle.setAttribute('aria-label', 'Закрыть меню');
    }

    if (this.elements.overlay) this.elements.overlay.classList.add('active');
    if (this.elements.panel) this.elements.panel.classList.add('active');

    this.lockScroll();
  }

  private close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    if (this.elements.toggle) {
      this.elements.toggle.setAttribute('aria-expanded', 'false');
      this.elements.toggle.setAttribute('aria-label', 'Открыть меню');
    }

    if (this.elements.overlay) this.elements.overlay.classList.remove('active');
    if (this.elements.panel) this.elements.panel.classList.remove('active');

    // Закрываем все подменю
    document.querySelectorAll('.mobile-submenu.active').forEach((submenu: Element) => {
      submenu.classList.remove('active');
      submenu.closest('.mobile-nav-item')?.classList.remove('active');
    });

    this.unlockScroll();
  }

  private lockScroll(): void {
    if (this.scrollLocked) return;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    this.body.style.paddingRight = `${scrollBarWidth}px`;
    this.html.style.overflow = 'hidden';
    this.body.style.overflow = 'hidden';
    this.scrollLocked = true;
  }

  private unlockScroll(): void {
    if (!this.scrollLocked) return;
    this.body.style.paddingRight = '';
    this.html.style.overflow = '';
    this.body.style.overflow = '';
    this.scrollLocked = false;
  }

  public destroy(): void {
    if (this.isOpen) this.close();
  }
}
